// src/pages/Auditorias/utils/formatacao.js

/**
 * Vocabulário de estados e gravidades, partilhado por toda a página.
 * Os valores crus vêm do motor Python — ver docs/auditoria-processos-spec.md.
 */

export const GRAVIDADE_CLASSE = {
  'Muito Grave': 'aud-grav-muito-grave',
  'Grave': 'aud-grav-grave',
  'Atencao': 'aud-grav-atencao',
  'OK': 'aud-grav-ok',
};

export const GRAVIDADE_LABEL = {
  'Muito Grave': 'muito grave',
  'Grave': 'grave',
  'Atencao': 'atenção',
  'OK': 'ok',
};

export const ESTADO_AUDITORIA_LABEL = {
  pendente: 'pendente',
  em_execucao: 'em execução',
  concluida: 'concluída',
  erro: 'erro',
  interrompida: 'interrompida',
};

/**
 * As etapas do motor, pela ordem em que correm.
 *
 * Há dois percursos, escolhidos pelo `avaliar_peca_individual` do mestre (que é
 * carimbado na criação e não muda). Sem os separar, uma auditoria em modo único
 * mostrava "etapa 3 de 4" e ficava eternamente numa quarta etapa que não corre.
 *
 * Ver AuditoriaService.executar() no projecto Python.
 */
export const ETAPAS = ['cronologia', 'transcricao', 'avaliacao', 'parecer'];
export const ETAPAS_PARECER_UNICO = ['cronologia', 'transcricao', 'parecer_unico'];

/** O percurso desta auditoria. `avaliar_peca_individual` ausente = modo profundo. */
export function etapasDoPercurso(dados) {
  return dados && dados.avaliar_peca_individual === false
    ? ETAPAS_PARECER_UNICO
    : ETAPAS;
}

export const ETAPA_LABEL = {
  cronologia: 'a reconstruir a cronologia',
  transcricao: 'a transcrever peças',
  avaliacao: 'a avaliar peças',
  parecer: 'a emitir o parecer',
  parecer_unico: 'a analisar o processo inteiro',
};

export const ESTADO_PECA_LABEL = {
  pendente: 'por processar',
  transcrito: 'transcrita',
  avaliado: 'avaliada',
  contexto: 'anexo',
  ignorado: 'ignorado na triagem',
  ilegivel: 'ilegível',
  erro: 'erro',
};

export function formatarData(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const hora = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dia}/${mes} ${hora}:${min}`;
}

/**
 * "há 2 h", "ontem", "há 5 min" — mais legível numa lista do que a data completa.
 */
export function formatarRelativo(iso) {
  if (!iso) return 'nunca';

  const segundos = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);

  if (segundos < 60) return 'agora mesmo';
  if (segundos < 3600) return `há ${Math.floor(segundos / 60)} min`;
  if (segundos < 86400) return `há ${Math.floor(segundos / 3600)} h`;
  if (segundos < 172800) return 'ontem';
  if (segundos < 2592000) return `há ${Math.floor(segundos / 86400)} dias`;
  return formatarData(iso);
}

export function formatarDuracao(segundos) {
  if (!segundos && segundos !== 0) return '—';
  if (segundos < 60) return `${segundos}s`;
  const min = Math.floor(segundos / 60);
  const seg = segundos % 60;
  return seg ? `${min}m ${seg}s` : `${min}m`;
}

export function formatarTamanho(chars) {
  if (!chars) return '';
  if (chars < 1024) return `${chars} car.`;
  return `${Math.round(chars / 1024)} KB`;
}

/**
 * A auditoria está viva? Determina se a página deve continuar a fazer polling.
 */
export function estaEmCurso(estado) {
  return estado === 'em_execucao' || estado === 'pendente';
}

/**
 * Descreve o que a auditoria está a fazer neste momento.
 *
 * Os contadores vêm das peças (pecas_lidas / avaliaveis), não dos totais do
 * mestre: esses só são recalculados no fim da execução e durante a corrida
 * mostrariam sempre 0.
 *
 * Devolve { etapa, contador, pormenor } — qualquer campo pode vir vazio.
 */
export function descreverProgresso(dados) {
  const etapa = dados.etapa_atual;
  const n = (v) => Number(v || 0);

  // Na fila: o worker é sequencial e pode estar ocupado com outra auditoria.
  // Dizer "a arrancar…" aqui é enganador — pode faltar meia hora.
  if (dados.fila_estado === 'pendente' || (!etapa && dados.fila_estado)) {
    const posicao = n(dados.fila_posicao);
    return {
      passo: '',
      etapa: posicao > 0
        ? `à espera de vez — ${posicao} ${posicao === 1 ? 'auditoria' : 'auditorias'} à frente`
        : 'à espera de vez',
      contador: '',
      pormenor: '',
    };
  }

  if (!etapa) {
    return { passo: '', etapa: 'a arrancar…', contador: '', pormenor: '' };
  }

  const etapas = etapasDoPercurso(dados);
  const indice = etapas.indexOf(etapa);

  const resultado = {
    passo: indice >= 0 ? `etapa ${indice + 1} de ${etapas.length}` : '',
    etapa: ETAPA_LABEL[etapa] || etapa,
    contador: '',
    pormenor: '',
  };

  if (etapa === 'transcricao' && n(dados.pecas_total)) {
    resultado.contador = `${n(dados.pecas_lidas)} de ${n(dados.pecas_total)}`;
  } else if (etapa === 'avaliacao' && n(dados.avaliaveis)) {
    resultado.contador = `${n(dados.avaliados)} de ${n(dados.avaliaveis)}`;
  }

  if (dados.ultima_peca) {
    resultado.pormenor = dados.ultima_peca_estado === 'ilegivel'
      ? `${dados.ultima_peca} — ilegível`
      : dados.ultima_peca;
  }

  return resultado;
}

/**
 * Percentagem concluída, ou null quando não há denominador.
 * A cronologia não tem contador — é uma chamada única de vários minutos.
 */
export function percentagemProgresso(dados) {
  const n = (v) => Number(v || 0);

  if (dados.etapa_atual === 'transcricao' && n(dados.pecas_total)) {
    return Math.round((n(dados.pecas_lidas) / n(dados.pecas_total)) * 100);
  }
  if (dados.etapa_atual === 'avaliacao' && n(dados.avaliaveis)) {
    return Math.round((n(dados.avaliados) / n(dados.avaliaveis)) * 100);
  }
  return null;
}
